import React, { useState } from "react";
import {
  CurrencyDollarIcon,
  CalendarIcon,
  GiftIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";
import BaseButtonSecondary from "../BaseButtonSecondary";
import BaseButtonPrimary from "../BaseButtonPrimary";
import BaseModal from "../BaseModal";
import BaseInputText from "../BaseInputText";
import {
  ExistingLoyaltyTransaction,
  getReadableTransactionStatus,
  getTransactionStatusColor,
  APIRecalculateLoyaltyResponse,
  TransactionStatus,
} from "../../models/loyalty-transaction";
import { ExistingLoyaltyProgramTemplate } from "../../models/loyalty-program-template";
import { convertToReadableTimestamp } from "../../assets/js/convertToReadableTimestamp";
import { DbRead, DbWrite } from "../../database";
import { logger } from "../../logging";
import { useToastMessageStore } from "../../store/toast-messages";
import { useSiteKeyDocStore } from "../../store/site-key-doc";
import { createToastMessageID } from "../../utils/createToastMessageID";

interface Props {
  transactions: ExistingLoyaltyTransaction[];
  loyaltyPrograms: ExistingLoyaltyProgramTemplate[];
  siteKeyID: string;
  invoiceID?: string;
  craftRecordID?: string;
  currency?: string;
}

export default function LoyaltyTransactionSummary({
  transactions,
  loyaltyPrograms,
  siteKeyID,
  invoiceID,
  craftRecordID,
  currency = "USD",
}: Props) {
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [processingTransaction, setProcessingTransaction] = useState<
    string | null
  >(null);

  // MoreRewards member number dialog state
  const [showMemberNumberDialog, setShowMemberNumberDialog] = useState(false);
  const [pendingTransactionForApproval, setPendingTransactionForApproval] =
    useState<string | null>(null);
  const [memberNumberInput, setMemberNumberInput] = useState("");
  const [isSavingMemberNumber, setIsSavingMemberNumber] = useState(false);

  const addMessage = useToastMessageStore((state) => state.addToastMessage);
  const siteKeyDoc = useSiteKeyDocStore((state) => state.siteKeyDoc);

  const handleRecalculate = async () => {
    setIsRecalculating(true);
    try {
      const response: APIRecalculateLoyaltyResponse =
        await DbWrite.loyaltyTransactions.recalculate({
          siteKey: siteKeyID,
          invoiceID,
          craftRecordID,
        });

      addMessage({
        id: createToastMessageID(),
        dialog: true,
        message: `Loyalty recalculation complete: ${response.transactionsCreated} created, ${response.transactionsUpdated} updated`,
        type: "success",
      });
    } catch (error) {
      logger.error("Error recalculating loyalty:", error);
      addMessage({
        id: createToastMessageID(),
        dialog: true,
        message: "Failed to recalculate loyalty transactions",
        type: "error",
      });
    } finally {
      setIsRecalculating(false);
    }
  };

  const handleApproveTransaction = async (transactionId: string) => {
    // Check if MoreRewards is configured and if customer has member number
    if (siteKeyDoc?.customizations?.moreRewards) {
      const transaction = transactions.find((t) => t.id === transactionId);
      if (transaction) {
        try {
          const customer = await DbRead.customers.get(
            siteKeyID,
            transaction.customerID,
          );
          if (!customer.customData?.moreRewardsMemberNumber) {
            // Show dialog to add member number
            setPendingTransactionForApproval(transactionId);
            setMemberNumberInput("");
            setShowMemberNumberDialog(true);
            return;
          }
        } catch (error) {
          logger.error("Error fetching customer for MoreRewards check:", error);
        }
      }
    }

    // Proceed with normal approval
    await approveTransactionDirectly(transactionId);
  };

  const approveTransactionDirectly = async (transactionId: string) => {
    setProcessingTransaction(transactionId);
    try {
      await DbWrite.loyaltyTransactions.approve({
        siteKey: siteKeyID,
        transactionID: transactionId,
        processingNote: "Approved via admin interface",
        sendEmail: true,
      });
      addMessage({
        id: createToastMessageID(),
        message: "Transaction approved successfully",
        type: "success",
        dialog: false,
      });
      logger.info("Transaction approved", {
        siteKey: siteKeyID,
        transactionId,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to approve transaction";
      addMessage({
        id: createToastMessageID(),
        message: errorMessage,
        type: "error",
        dialog: true,
      });
      logger.error("Error approving transaction:", error);
    } finally {
      setProcessingTransaction(null);
    }
  };

  const handleRejectTransaction = async (transactionId: string) => {
    setProcessingTransaction(transactionId);
    try {
      await DbWrite.loyaltyTransactions.reject({
        siteKey: siteKeyID,
        transactionID: transactionId,
        processingNote: "Rejected via admin interface",
      });
      addMessage({
        id: createToastMessageID(),
        message: "Transaction rejected successfully",
        type: "success",
        dialog: false,
      });
      logger.info("Transaction rejected", {
        siteKey: siteKeyID,
        transactionId,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to reject transaction";
      addMessage({
        id: createToastMessageID(),
        message: errorMessage,
        type: "error",
        dialog: true,
      });
      logger.error("Error rejecting transaction:", error);
    } finally {
      setProcessingTransaction(null);
    }
  };

  const handleSaveMemberNumber = async () => {
    if (!pendingTransactionForApproval || !memberNumberInput.trim()) {
      return;
    }

    setIsSavingMemberNumber(true);
    try {
      const transaction = transactions.find(
        (t) => t.id === pendingTransactionForApproval,
      );
      if (transaction) {
        const customer = await DbRead.customers.get(
          siteKeyID,
          transaction.customerID,
        );

        // Update customer with MoreRewards member number
        await DbWrite.customers.update({
          ...customer,
          customData: {
            ...customer.customData,
            moreRewardsMemberNumber: memberNumberInput.trim(),
          },
        });

        setShowMemberNumberDialog(false);
        addMessage({
          id: createToastMessageID(),
          message: "MoreRewards member number added successfully",
          type: "success",
          dialog: false,
        });

        // Now approve the transaction
        await approveTransactionDirectly(pendingTransactionForApproval);
      }
    } catch (error) {
      logger.error("Error saving MoreRewards member number:", error);
      addMessage({
        id: createToastMessageID(),
        message: "Failed to save MoreRewards member number",
        type: "error",
        dialog: true,
      });
    } finally {
      setIsSavingMemberNumber(false);
      setPendingTransactionForApproval(null);
      setMemberNumberInput("");
    }
  };

  const handleCancelMemberNumberDialog = () => {
    setShowMemberNumberDialog(false);
    setPendingTransactionForApproval(null);
    setMemberNumberInput("");
  };

  if (transactions.length === 0) {
    return (
      <div className="border-t border-gray-200 pt-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Loyalty Rewards</h3>
          <BaseButtonSecondary
            onClick={handleRecalculate}
            isBusy={isRecalculating}
            busyText="Recalculating..."
            className="ml-4"
          >
            <ArrowPathIcon className="mr-2 h-4 w-4" />
            Update Loyalty Transactions
          </BaseButtonSecondary>
        </div>
        <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
          <p className="text-sm text-gray-600">
            No loyalty rewards have been earned{" "}
            {invoiceID ? "from this invoice" : "for this job"} yet.
          </p>
        </div>
      </div>
    );
  }

  // Calculate totals - only include approved and redeemed transactions
  const activeTransactions = transactions.filter(
    (t) =>
      t.status === TransactionStatus.APPROVED ||
      t.status === TransactionStatus.REDEEMED,
  );

  const totalPoints = activeTransactions.reduce(
    (sum, t) => sum + (t.pointsEarned || 0),
    0,
  );
  const totalCashback = activeTransactions.reduce(
    (sum, t) => (t.rewardType === "commission" ? sum + t.rewardValue : sum),
    0,
  );
  const totalDiscounts = activeTransactions.reduce(
    (sum, t) =>
      t.rewardType === "percentageDiscount" || t.rewardType === "fixedDiscount"
        ? sum + t.rewardValue
        : sum,
    0,
  );

  const getProgramName = (programID: string): string => {
    const program = loyaltyPrograms.find((p) => p.id === programID);
    return program?.name || "Unknown Program";
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  return (
    <div className="border-t border-gray-200 pt-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Loyalty Rewards</h3>
        <BaseButtonSecondary
          onClick={handleRecalculate}
          isBusy={isRecalculating}
          busyText="Recalculating..."
          className="ml-4"
        >
          Recalculate
        </BaseButtonSecondary>
      </div>

      {/* Summary Cards */}
      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {totalPoints > 0 && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-center">
              <GiftIcon className="h-6 w-6 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-blue-900">
                  Points Earned
                </p>
                <p className="text-2xl font-bold text-blue-700">
                  {totalPoints}
                </p>
              </div>
            </div>
          </div>
        )}

        {totalCashback > 0 && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-4">
            <div className="flex items-center">
              <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-green-900">
                  Cashback Earned
                </p>
                <p className="text-2xl font-bold text-green-700">
                  {formatCurrency(totalCashback)}
                </p>
              </div>
            </div>
          </div>
        )}

        {totalDiscounts > 0 && (
          <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
            <div className="flex items-center">
              <CalendarIcon className="h-6 w-6 text-purple-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-purple-900">
                  Discounts Earned
                </p>
                <p className="text-2xl font-bold text-purple-700">
                  {totalDiscounts}
                  {activeTransactions.some(
                    (t) => t.rewardType === "percentageDiscount",
                  ) && "%"}
                  {activeTransactions.some(
                    (t) => t.rewardType === "fixedDiscount",
                  ) && formatCurrency(totalDiscounts).replace(/[0-9,]/g, "")}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Transaction Details */}
      <div className="space-y-3">
        {transactions.map((transaction) => (
          <div
            key={transaction.id}
            className="rounded-lg border border-gray-200 bg-white p-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-900">
                    {getProgramName(transaction.loyaltyProgramID)}
                  </h4>
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getTransactionStatusColor(
                        transaction.status,
                      )}`}
                    >
                      {getReadableTransactionStatus(transaction.status)}
                    </span>
                    {transaction.status === TransactionStatus.PENDING && (
                      <div className="flex gap-1">
                        <button
                          onClick={() =>
                            handleApproveTransaction(transaction.id)
                          }
                          disabled={processingTransaction === transaction.id}
                          className="inline-flex items-center rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-200 disabled:opacity-50"
                        >
                          <CheckCircleIcon className="mr-1 h-3 w-3" />
                          Approve
                        </button>
                        <button
                          onClick={() =>
                            handleRejectTransaction(transaction.id)
                          }
                          disabled={processingTransaction === transaction.id}
                          className="inline-flex items-center rounded bg-red-100 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-200 disabled:opacity-50"
                        >
                          <XCircleIcon className="mr-1 h-3 w-3" />
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-1 flex items-center space-x-4 text-sm text-gray-600">
                  <span>
                    Earned: {convertToReadableTimestamp(transaction.earnedDate)}
                  </span>
                  <span>
                    Source: {formatCurrency(transaction.sourceAmount)}
                  </span>
                </div>
                <div className="mt-2 text-sm">
                  {transaction.rewardType === "points" && (
                    <span className="font-medium text-blue-700">
                      {transaction.pointsEarned || 0} points
                    </span>
                  )}
                  {transaction.rewardType === "commission" && (
                    <span className="font-medium text-green-700">
                      {formatCurrency(transaction.rewardValue)} cashback
                    </span>
                  )}
                  {transaction.rewardType === "percentageDiscount" && (
                    <span className="font-medium text-purple-700">
                      {transaction.rewardValue}% discount
                    </span>
                  )}
                  {transaction.rewardType === "fixedDiscount" && (
                    <span className="font-medium text-purple-700">
                      {formatCurrency(transaction.rewardValue)} discount
                    </span>
                  )}
                </div>
                {transaction.expirationDate && (
                  <div className="mt-1 text-xs text-gray-500">
                    Expires:{" "}
                    {convertToReadableTimestamp(transaction.expirationDate)}
                  </div>
                )}
                {transaction.processingNote && (
                  <div className="mt-1 text-xs italic text-gray-600">
                    Note: {transaction.processingNote}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* MoreRewards Member Number Dialog */}
      <BaseModal
        open={showMemberNumberDialog}
        closeModal={handleCancelMemberNumberDialog}
        title={<></>}
        parentDivStyles="text-left max-w-md"
      >
        <div className="mb-4 flex w-full items-center justify-between rounded-t-lg bg-primary p-8 text-left text-white">
          <div className="inline-flex items-center gap-3">
            <img
              src="/moreRewards_logo.svg"
              alt="MoreRewards"
              className="h-8"
            />
            <h1 className="text-xl font-medium">Add Member Number</h1>
          </div>
          <button type="button" onClick={handleCancelMemberNumberDialog}>
            <XMarkIcon
              aria-label="close member number dialog"
              className="h-6 text-white"
            />
          </button>
        </div>

        <div className="space-y-4 p-6">
          <p className="text-sm text-gray-600">
            This customer needs a MoreRewards member number to process loyalty
            rewards. Please add their member number to continue with approval.
          </p>

          <div>
            <div className="mb-2 flex items-center gap-2">
              <img
                src="/moreRewards_logo.svg"
                alt="MoreRewards"
                className="h-4"
              />
              <span className="text-sm font-medium text-gray-700">
                Member Number
              </span>
            </div>
            <BaseInputText
              text=""
              inputName="memberNumber"
              admin={true}
              required={true}
              value={memberNumberInput}
              onChange={(e) => setMemberNumberInput(e.target.value)}
              placeholder="Enter member number"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <BaseButtonSecondary
              onClick={handleCancelMemberNumberDialog}
              disabled={isSavingMemberNumber}
            >
              Cancel
            </BaseButtonSecondary>
            <BaseButtonPrimary
              onClick={handleSaveMemberNumber}
              disabled={!memberNumberInput.trim() || isSavingMemberNumber}
              isBusy={isSavingMemberNumber}
              busyText="Saving..."
            >
              Save & Approve Transaction
            </BaseButtonPrimary>
          </div>
        </div>
      </BaseModal>
    </div>
  );
}
